import { supabaseClient } from "@/lib/supabase";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { toast } from "sonner";

export interface SignupProps {
	email: string;
	password: string;
	options: {
		data?: Record<string, unknown>;
		emailRedirectTo?: string;
		redirectTo?: string;
	};
}

export const signup = async (args: SignupProps) => {
	const { data, error } = await supabaseClient.auth.signUp(args);
	if (error) throw error;
	return data;
};

import { useNavigate } from "@tanstack/react-router";
import { router } from "../../../main";
import type { SignupSchemaType } from "../types/signupSchema";
import { createRedirectUrl } from "../utils/createRedirectUrl";

const usePasswordSignupMutation = () => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	return useMutation({
		mutationKey: ["signup"],
		mutationFn: async (formData: SignupSchemaType) => {
			try {
				// Ensure we have all required data
				if (!formData.email || !formData.password) {
					throw new Error("Email and password are required");
				}

				navigate({ to: "/verify", search: { email: formData.email } });

				return await signup({
					email: formData.email,
					password: formData.password,
					options: {
						data: formData.metadata,
						emailRedirectTo: createRedirectUrl("/onboarding/welcome"),
						redirectTo: createRedirectUrl("/onboarding/welcome"),
					},
				});
			} catch (error) {
				// Ensure all errors are caught and handled
				console.error("Error in signup mutation:", error);
				throw error; // Re-throw to be handled by onError
			}
		},
		onError: (error) => {
			console.error("Signup error:", error);
			toast.error(error.message || "An error occurred during signup");
		},
		onSuccess: (data) => {
			console.log("Signup successful", data);

			if (!data?.user) {
				console.warn("Signup successful but no user data returned");
			}

			toast.success("Sign-up successful");

			router.navigate({
				to: "/verify",
				search: { email: data.user?.email || "" },
			});
			// Navigation will be handled by the component
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["profile"] });
			queryClient.invalidateQueries({ queryKey: ["user"] });
		},
	});
};

export default usePasswordSignupMutation;

export const useRequestTransfer = () => {
	return useMutation({
		mutationKey: ["transfer"],
		mutationFn: async (formData: SignupSchemaType) => {
			try {
				return await signup({
					email: formData.email,
					password: formData.password,
					options: { data: formData.metadata },
				});
			} catch (error) {
				console.error("Error in transfer request:", error);
				throw error; // Re-throw to be handled by onError
			}
		},
		onError: (error) => {
			toast.error(error.message || "An error occurred during transfer request");
		},
		onSuccess: (data) => {
			toast.success(
				"Transfer requested - please check your email to ensure it went through.",
			);
			supabaseClient.functions.invoke("public-email", {
				body: {
					to_email: "info@patient-watch.com",
					cc_email: data.user?.email,
					from_email: "notification@reminders.footwatch.co.uk",
					subject: "Transfer to New Patient Watch",
					body: `Transfer request for email ${
						data.user?.email
					} at ${dayjs().toString()}`,
				},
			});
		},
	});
};
